import { useEffect, useState } from "react";

/**
 * Custom hook for detecting user's reduced motion preference.
 *
 * Features:
 * - Detects system reduced motion preference
 * - Listens for changes in system motion preference
 * - Uses `MediaQueryList#addEventListener("change")` when available, with `addListener` / `removeListener` fallback for older browsers
 * - Handles SSR environments gracefully
 * - Automatically cleans up event listeners
 *
 * @returns Object containing whether reduced motion is preferred
 */
export const usePrefersReducedMotion = (): {
  prefersReducedMotion: boolean;
} => {
  const [prefersReducedMotion, setPrefersReducedMotion] =
    useState<boolean>(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    const sync = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    sync();

    const handleModern = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleModern);
      return () => {
        mediaQuery.removeEventListener("change", handleModern);
      };
    }

    const handleLegacy = () => {
      sync();
    };

    mediaQuery.addListener(handleLegacy);
    return () => {
      mediaQuery.removeListener(handleLegacy);
    };
  }, []);

  return {
    prefersReducedMotion,
  };
};
